import { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { MapPin, Mail, Phone, Clock, Send, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import { formsAPI } from '../lib/api';
import SEO from '../components/common/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    studentName: '',
    email: '',
    phone: '',
    studentType: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await formsAPI.submitContact(formData);
      toast.success(response.data.message || "Thank you! We'll be in touch soon.");
      setFormData({
        parentName: '',
        studentName: '',
        email: '',
        phone: '',
        studentType: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO page="contact" />
      <Navbar />
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7000FF]/20 via-transparent to-[#FF007F]/20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="font-accent text-base tracking-[0.3em] text-[#FF007F] uppercase">Get In Touch</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4 mb-4">
            CONTACT <span className="text-[#FF007F]">US</span>
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Ready to start your performing arts journey? We'd love to hear from you!
          </p>
        </div>
      </section>

      <section className="py-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="bg-[#111] border-[#222]">
              <CardHeader>
                <CardTitle className="font-heading text-2xl text-white uppercase">Make an Enquiry</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="studentType" className="text-white/70">Student Type *</Label>
                    <Select 
                      value={formData.studentType} 
                      onValueChange={(v) => handleChange('studentType', v)}
                    >
                      <SelectTrigger data-testid="student-type-select" className="bg-[#0a0a0a] border-[#333] text-white">
                        <SelectValue placeholder="Select program" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border-[#333]">
                        <SelectItem value="primatotterina">Primatotterina (Little Ones)</SelectItem>
                        <SelectItem value="upa">Universal Performing Arts - UPA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="parentName" className="text-white/70">Parent's Name *</Label>
                    <Input
                      id="parentName"
                      placeholder="Your full name"
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                      value={formData.parentName}
                      onChange={(e) => handleChange('parentName', e.target.value)}
                      required
                      data-testid="parent-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="studentName" className="text-white/70">Student's Name *</Label>
                    <Input
                      id="studentName"
                      placeholder="Student's full name"
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                      value={formData.studentName}
                      onChange={(e) => handleChange('studentName', e.target.value)}
                      required
                      data-testid="student-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white/70">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        data-testid="email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white/70">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="Your phone number"
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white/70">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your interests and any questions you have..."
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      rows={4}
                      data-testid="message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold uppercase tracking-wider rounded-none"
                    disabled={submitting}
                    data-testid="submit-contact-btn"
                  >
                    {submitting ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info & Map */}
            <div className="space-y-6">
              <Card className="bg-[#111] border-[#222]">
                <CardContent className="p-6 space-y-6">
                  {/* Saturday Classes Location */}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#FF007F]" />
                      SATURDAY CLASSES
                    </h3>
                    <div className="text-white/70 space-y-1">
                      <p className="font-semibold text-white">Kents Hill Secondary School</p>
                      <p>Kents Hill Park</p>
                      <p>Milton Keynes</p>
                      <p>MK7 6BZ</p>
                    </div>
                    <p className="mt-3 text-sm text-white/50">
                      When you arrive, park in the car park and walk down the road with bike sheds 
                      on your right and courts on your left. Follow to the end and we are on the 
                      right through the 2nd doors.
                    </p>
                  </div>

                  {/* Weekday Classes Location */}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-[#7000FF]" />
                      WEEKDAY CLASSES
                    </h3>
                    <div className="text-white/70 space-y-1">
                      <p className="font-semibold text-white">Watling Academy</p>
                      <p>Barrosa Way</p>
                      <p>Whitehouse</p>
                      <p>Milton Keynes, MK8 1EP</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-[#FF007F]" />
                      PHONE
                    </h3>
                    <a 
                      href="tel:07310105678" 
                      className="text-[#FF007F] hover:underline font-medium flex items-center gap-2"
                    >
                      07310 105678
                    </a>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#FF007F]" />
                      EMAIL
                    </h3>
                    <a 
                      href="mailto:theteam@upaschool.com" 
                      className="text-[#FF007F] hover:underline font-medium flex items-center gap-2"
                    >
                      theteam@upaschool.com
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#FF007F]" />
                      CLASS TIMES
                    </h3>
                    <p className="text-white/70">
                      Classes run on weekday evenings and Saturday mornings. 
                      Check our <a href="/schedule" className="text-[#FF007F] hover:underline">schedule</a> for specific times.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Map */}
              <Card className="overflow-hidden border-[#222] bg-[#111]">
                <div className="h-80">
                  <iframe
                    src="https://maps.google.com/maps?q=Kents+Hill+Secondary+School+Milton+Keynes+MK7+6BZ&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="UPA Location Map"
                  />
                </div>
              </Card>

              {/* Quick Links */}
              <div className="flex gap-4">
                <Button 
                  variant="outline" 
                  className="flex-1 border-[#333] text-white hover:bg-[#222] rounded-none"
                  onClick={() => window.open('https://maps.google.com/maps?q=Kents+Hill+Secondary+School+Milton+Keynes+MK7+6BZ', '_blank')}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Get Directions
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 border-[#333] text-white hover:bg-[#222] rounded-none"
                  onClick={() => window.location.href = 'mailto:theteam@upaschool.com'}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
